import { useCallback, useEffect, useRef } from 'react'
import { useAgentRuntime } from '../context/AgentProvider'
import type { PageContextInput } from '../config/types'

export type PageContextProvider = (input: PageContextInput) => Record<string, unknown> | undefined

/**
 * Register per-page context metadata for the built-in `pageContext` attachment.
 *
 * Use this inside route/page components to attach route-specific context (record id, filters, etc.).
 * Data returned here is merged (shallow) into `attachment.metadata.data` alongside `config.pageContext.getData`.
 *
 * Example:
 *
 * ```ts
 * usePageContext(() => ({ recordId }), [recordId])
 * ```
 */
export const usePageContext = (provider: PageContextProvider, deps: unknown[] = []) => {
  const runtime = useAgentRuntime()
  const providerRef = useRef<PageContextProvider>(provider)

  const memoProvider = useCallback(provider, deps)

  providerRef.current = memoProvider

  const stableProvider = useCallback<PageContextProvider>((input) => providerRef.current(input), [])

  useEffect(() => {
    return runtime.registerPageContextProvider(stableProvider)
  }, [runtime, stableProvider])
}
